// SHARED CODE WITH FRONTEND

declare global {
  interface String {
    notEmpty(): boolean;
    capitalize(): string;
    capitalizeFirstLetter(): string;
  }
}

export function pad00(n: number): string {
  let t = Math.abs(n).toString();
  if (t.length < 2) {
    t = `0${t}`;
  }
  return n < 0 ? `-${t}` : t;
}

export function pad000(n: number): string {
  let t = Math.abs(n).toString();
  if (t.length < 2) {
    t = `00${t}`;
  }
  if (t.length < 3) {
    t = `0${t}`;
  }
  return n < 0 ? `-${t}` : t;
}

String.prototype.notEmpty = function (this: string): boolean {
  return Boolean(this) && this.length > 0;
};

String.prototype.capitalize = function (this: string): string {
  if (!this || this.length === 0) {
    return this;
  }
  return this.charAt(0).toUpperCase() + this.slice(1);
};

String.prototype.capitalizeFirstLetter = function (this: string): string {
  return this.charAt(0).toLocaleUpperCase() + this.slice(1);
};

export const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const makeGlobal = 0;

const picUrlRegex = /(imgix\.|\.jpe?g|\.png)/;

export const isNotVideo = (url?: string | null): boolean => {
  if (url && picUrlRegex.exec(url)) {
    return true;
  }
  return false;
};

export function sortString(
  a: string | Record<string, string>,
  b: string | Record<string, string>,
  opts?: { property?: string; desc?: boolean }
): number {
  const nameA = opts?.property
    ? (a as Record<string, string>)[opts.property].toLowerCase()
    : (a as string).toLowerCase();
  const nameB = opts?.property
    ? (b as Record<string, string>)[opts.property].toLowerCase()
    : (b as string).toLowerCase();
  if (nameA < nameB) {
    return opts?.desc ? 1 : -1;
  }
  if (nameA > nameB) {
    return opts?.desc ? -1 : 1;
  }
  return 0;
}

const regexUrl = new RegExp(
  '^' +
    // protocol identifier
    '(?:(?:https?|ftp)://)' +
    // user:pass authentication (optional)
    '(?:\\S+(?::\\S*)?@)?' +
    '(?:' +
    // IP address exclusion
    // private & local networks
    '(?!(?:10|127)(?:\\.\\d{1,3}){3})' +
    '(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})' +
    '(?!172\\.(?:1[6-9]|2[0-9]|3[0-1])(?:\\.\\d{1,3}){2})' +
    // IP address dotted notation octets
    // excludes loopback network 0.0.0.0
    // excludes reserved space >= 224.0.0.0
    // excludes network & broadcast addresses
    // (first & last IP address of each class)
    '(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])' +
    '(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}' +
    '(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))' +
    '|' +
    // host & domain names, may end with dot
    '(?:' +
    '(?:' +
    '[a-z0-9\\u00a1-\\uffff]' +
    '[a-z0-9\\u00a1-\\uffff_-]{0,62}' +
    ')?' +
    '[a-z0-9\\u00a1-\\uffff]\\.' +
    ')+' +
    // TLD identifier
    '(?:[a-z\\u00a1-\\uffff]{2,}\\.?)' +
    ')' +
    // port number (optional)
    '(?::\\d{2,5})?' +
    // resource path (optional)
    '(?:[/?#]\\S*)?' +
    '$',
  'i'
);

export const isValidUrl = (url: string): boolean => {
  if (regexUrl.exec(url)) {
    return true;
  }
  return false;
};

const regexHTML = new RegExp('(</([^>]+)>)|&nbsp;');

export const isHtmlFormatting = (value: string): boolean => {
  if (regexHTML.exec(value)) {
    return true;
  }
  return false;
};

const regexMarkdown =
  /(#([^\*]+))|(\[([^\]]+)\]\(([^\)]+)\))|(\*\*([^\*]+)\*\*)|(\*([^\*]+)\*)|(\+\+([\s\S]+?)\+\+(?!\+))/g;

export const isMarkdownFormatting = (value: string): boolean => {
  if (regexMarkdown.exec(value)) {
    return true;
  }
  return false;
};

export const hash = (value?: string): number => {
  if (!value) {
    return 0;
  }

  let hash = 0,
    i,
    chr;
  for (i = 0; i < value.length; i++) {
    chr = value.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

export const doesExist = (value?: any): boolean => {
  if (!value || value === undefined || value === '' || !Object.keys(value).length) return false;
  return true;
};

export const stringToMarkdown = (value: string): string => {
  return value
    .replace(/([^\n]+)([\n])/g, (_match: string, p1: string, _p2: string) => `${p1}#nl##nl#`)
    .replace(/\n/g, '\u200b#nl##nl#')
    .replace(/#nl#/g, '\n');
};

export const isBase64 = (value: string): boolean => {
  if (!value) return false;
  if (value.startsWith('data:')) {
    return true;
  } else {
    return false;
  }
};

export const maxCharsMessage = (maxChars: number): string => {
  return `Please limit to ${maxChars} characters or less.`;
};

export const getMaxCharsMessage = (maxChars?: number, value?: string): string | null => {
  const hasEndCharacter = value && value.charAt(value.length - 1) === '\n' ? true : false;
  return maxChars && value && value.length > (hasEndCharacter ? maxChars + 1 : maxChars)
    ? maxCharsMessage(maxChars)
    : null;
};

/**
 * Do not allow empty string for propertyName
 * @param value
 * @returns
 */
export const normalizePropertyName = (value?: string | null): string | null | undefined => {
  if (value === undefined || value === null) return value;
  value = value.trim();
  return value === '' ? null : value;
};

export const isValidDomain = (domain: string): boolean => {
  const regex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
  if (regex.test(domain)) {
    return true;
  }
  return false;
};

export const removeZeroWidthChar = (str: string): string => {
  return str.replace(/[\u200B-\u200D\uFEFF]/g, '');
};

export const encodeBase64UrlParam = (str: string): string => {
  // inner encodeURIComponent escapes Unicode characters
  // outer encodeURIComponent escapes '/' characters
  return encodeURIComponent(btoa(encodeURIComponent(str)));
};

export const decodeBase64UrlParam = (str: string): string | null => {
  try {
    return decodeURIComponent(atob(decodeURIComponent(str)));
  } catch (e) {
    return null;
  }
};

export function isFromDomain(url: string, domain: string): boolean {
  const urlObj = new URL(url);
  return urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`);
}

export const extractPathFromUrl = (url: string): string => {
  const domainPaths = url.split('://');
  if (!domainPaths[1]) return '';

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_domain, ...paths] = domainPaths[1].split('/');
  const path = paths.join('/');
  return path;
};

export const extractDomainFromEmail = (email: string): string | null => {
  // Known public email domains
  const publicDomains = [
    'gmail.com',
    'yahoo.com',
    'outlook.com',
    'hotmail.com',
    'live.com',
    'icloud.com',
    'aol.com',
    'yandex.com',
    'mail.com',
    'zoho.com',
  ];

  // Extract the domain from the email
  const domain = email.split('@')[1];

  // Check if the domain is in the list of public email providers
  if (publicDomains.includes(domain)) return null;

  return domain;
};
